/* eslint-disable no-prototype-builtins */
/* eslint-disable operator-linebreak */
/* eslint-disable no-case-declarations */
import { cloneDeep, get, isEqual, set } from 'lodash-es';
import { v4 } from 'uuid';
import dayjs from 'dayjs';
import { applySnapshot, cast, getSnapshot, isStateTreeNode, types, } from 'mobx-state-tree';
import jsonData from './microcasting.schema';
import pluralize from './pluralize';
import { parseAwsJsonObject } from './parseAwsJson';
import { ChimeAttendeeType, ChimeMediaPlacementType, ChimeMeetingType } from './ChimeTypes';
const defaults = {};
export const JsonType = types.union(types.string, types.number, types.integer, types.Date, types.boolean, types.map(types.late(() => JsonType)), types.array(types.late(() => JsonType)), types.undefined, types.null);
// @TODO: limit format (uppercase alpha only, 8 chars max)
export const AccessGroupType = types.array(types.string);
/**
 * Notes on the defintions here:
 * stubbed - created with ID but not saved or loaded.
 * saving - in process of sending update to server (may be queued)
 * loading - in process of receiving update from server (may be queued)
 * loaded - moded is populated with data
 * not_found - load request resulted in not finding an item with that id
 * error - some other error - check logs for details.
 */
export const SyncStateType = types.optional(types.enumeration('SyncState', ['stubbed', 'saving', 'loading', 'loaded', 'not_found', 'error']), 'stubbed');
export const rootTypes = new Map();
// Only the AI Configuration screen uses this unprojected load source.
const portalAIConfigSources = new WeakMap();
const portalAIConfigEdits = new WeakMap();
const getPortalAIConfigEdits = (portal) => {
    if (!portal)
        return new Map();
    let edits = portalAIConfigEdits.get(portal);
    if (!edits) {
        edits = new Map();
        portalAIConfigEdits.set(portal, edits);
    }
    return edits;
};
/**
 * Decouples dependencies between RootStore and
 * Models that need the resolver to look up related objects.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
let _resolver;
export const getResolver = () => (_resolver);
export const setResolver = (resolver) => { _resolver = resolver; };
/**
 * Custom Reference type that resolves via a call to look up the item in the db.
 *
 * Flow is - 1) if found, return the item
 * 2) if not not found, return empty item with that ID, in pending state
 * 3) trigger the load of the item from the data source
 * 4) when item is loaded from data source it gets merged in to the item
 * ( 5) screen should refresh as item is loaded )
 */
export const resolvableReferenceType = (references) => (types.late(() => {
    const rootType = rootTypes.get(references);
    if (!rootType) {
        throw Error(`No entry for ${references} in Schema.rootTypes`);
    }
    return types.reference(rootType, {
        get(identifier) {
            // console.log(`Calling get or load for ${references} with key ${identifier}`);
            return getResolver().getOrLoad(references, identifier);
        },
        set(value) {
            return value.key;
        },
    });
}));
const getDateType = (required) => (required ? types.Date : types.maybeNull(types.Date));
const isString = (value) => (typeof value === 'string' || value instanceof String);
/**
 *
 * @param typeName
 * @param required
 * @param references
 * @returns
 */
const getPropType = (typeName, required = false, references = null, nullable = false, defaultValue = undefined) => {
    // console.log('getPropType:', typeName);
    switch (typeName) {
        case 'reference':
            const referenceType = resolvableReferenceType(references);
            const requiredType = required ? referenceType : types.maybeNull(referenceType);
            return requiredType;
        // return nullable ? types.maybeNull(requiredType) : requiredType;
        // This type is when the array of items is owned by the object and doesnt exist outside of it
        case 'array':
            const arrayReferenceType = references === 'string' ? types.string : types.late(() => rootTypes.get(references));
            // // if (!arrayReferenceType) {
            // //   throw new Error(`Reference type ${references} not found`);
            // // }
            const arrayType = types.array(arrayReferenceType);
            return required ? arrayType : types.optional(arrayType, []);
        // This type is an array where each item is a reference to a seperate model
        case 'dependant':
            const dependantType = types.late(() => rootTypes.get(references));
            return required ? dependantType : types.maybeNull(dependantType);
        // This type is an array where each item is a reference to a seperate model
        case 'join':
            const joinArrayType = types.array(resolvableReferenceType(references));
            return types.optional(joinArrayType, []);
        case 'referenceArray':
            const refArrayType = types.array(resolvableReferenceType(references));
            return required ? refArrayType : types.optional(refArrayType, []);
        case 'number':
            return required ? types.number : types.maybeNull(types.number);
        case 'currency':
            return required ? types.string : types.optional(types.string, '');
        case 'string':
            // // maybe null is the right choice because that is what the graphql responds with
            // // if the field has not been set.
            // // return required ? types.string : types.maybeNull(types.string);
            // Using map than snap to get arround this so this will work...
            const stringType = required ? types.string : types.optional(types.string, '');
            return nullable ? types.maybeNull(stringType) : stringType;
        case 'boolean':
            // // maybe null is the right choice because that is what the graphql responds with
            // // if the field has not been set.
            // // return required ? types.boolean : types.maybeNull(types.boolean);
            // Using map than snap to get arround this so this will work...
            return required ? types.boolean : types.optional(types.boolean, Boolean(defaultValue));
        case 'date':
            return getDateType(required);
        case 'timestamp':
            return getDateType(required);
        case 'json':
            // return required ? types.string : types.optional(types.string,'');
            return required
                ? types.frozen(JsonType)
                : types.optional(types.frozen(JsonType), defaultValue || {});
        // case 'accessGroup':
        //  return required ? types.string : types.optional(types.string,'');
        //  return required ? AccessGroupType : types.optional(AccessGroupType, '');
        case 'richtext':
            // return required ? JsonType : types.optional(JsonType, null);
            // special format here - { 'slate': {}, html: "" }
            return types.frozen(JsonType);
        case 'formattedText':
            return required ? types.string : types.optional(types.string, '');
        case 'object': // TODO - is this right?
            return types.model;
        case 'quizQuestions': // TODO - is this right?
            return types.frozen(JsonType);
        case 'courseCertificateCriteria': // TODO - is this right?
            return types.frozen(JsonType);
        case 'quizCertificateCriteria': // TODO - is this right?
            return types.frozen(JsonType);
        case 'accessGroups':
            return AccessGroupType;
        case 'chimeMediaPlacement':
            return ChimeMediaPlacementType;
        case 'chimeAttendee':
            return types.maybe(ChimeAttendeeType);
        case 'chimeMeeting':
            return types.maybe(ChimeMeetingType);
        case 'options':
            return types.array(types.string);
        default:
            throw new Error(`Unknown field type ${typeName}`);
    }
};
export const generateType = (entityType, overrides = null) => {
    // could narrow the 'any' below to just the types our schema supports
    const newModel = {};
    if (entityType.type === 'entity') {
        newModel.syncState = SyncStateType;
        newModel.loadedAt = types.optional(types.number, 0);
    }
    newModel.key = types.optional(types.identifier, () => v4());
    Object.keys(entityType.properties).forEach((prop) => {
        // console.log(`prop name: ${prop}`, entityType.properties[prop]);
        let propType = null;
        // if (prop === 'id') {
        //   propType = types.identifier;
        // } else
        if (overrides && overrides.has(prop)) {
            propType = overrides.get(prop);
        }
        else {
            propType = getPropType(entityType.properties[prop].type, (entityType.required && entityType.required.includes(prop)), entityType.properties[prop].references, entityType.properties[prop].nullable, entityType.properties[prop].default);
        }
        if (prop === 'organizationID') {
            // console.log('Skipping organizationID');
        }
        else {
            newModel[prop] = propType;
        }
    });
    return types.model(entityType.name, newModel)
        .volatile(() => ({
        // syncState: 'stubbed',
        changeCount: 0, // types.optional(types.integer,0),
        // time when the item was last loaded from the server
        // loadedAt: 0,
        loadError: undefined,
    }))
        .actions((self) => ({
        resetLoadedAt() {
            self.loadedAt = Date.now();
            self.syncState = 'loaded';
            self.loadError = undefined;
        },
        setNotFound() {
            self.loadedAt = Date.now();
            self.syncState = 'not_found';
            self.name = '(not found)';
            self.loadError = undefined;
        },
        setLoadError(error) {
            self.loadedAt = Date.now();
            self.syncState = 'error';
            self.loadError = error;
        },
        addChange() {
            self.changeCount += 1;
        },
        resetChange() {
            self.changeCount = 0;
        },
        updateFields(fields, newValues) {
            const changeCount = applyFormFieldUpdatesToMst(self, fields, newValues);
            if (changeCount > 0) {
                self.addChange();
            }
        },
        updateValues(values) {
            // takes a bunch of values and updates this instance with them
            if (!values) {
                return;
            }
            Object.keys(values).forEach((key) => {
                try {
                    if (key !== 'key' && key !== 'id' && self.hasOwnProperty(key) && self[key] !== values[key]) {
                        self[key] = values[key];
                    }
                }
                catch (e) {
                    console.log('Error on update values: ', { item: getSnapshot(self), key, values });
                }
            });
            this.addChange();
        },
    }));
};
// eslint-disable-next-line arrow-body-style
export const isOrganizationScoped = (modelName) => {
    const orgScoped = [
        'asset',
        'certificate',
        'certification',
        'clientAi',
        'course',
        'courseProgress',
        'customer',
        'document',
        'documentProgress',
        'emailTemplate',
        'engagementFlow',
        'hub',
        'instructor',
        'invite',
        'itemProgress',
        'lead',
        'learningBoard',
        'library',
        'libraryShare',
        'repository',
        'meeting',
        'meetingRegistration',
        'member',
        'membership',
        'microcast',
        'microcastProgress',
        'payment',
        'portal',
        'product',
        'ecommerceCategory',
        'webPage',
        'program',
        'programProgress',
        'questionnaire',
        'question',
        'shopifyConfig',
        'stylesheet',
        'userOrganization',
        'kicker',
        'discussionTopic',
        'discussionPost',
    ].includes(modelName);
    // console.log(`${modelName} is org scoped:`, orgScoped);
    return orgScoped;
};
/** Model names for which pgvector search returns full item (display_json). Skip batchGetModel for these.
 * Must match FULL_DISPLAY_JSON_TYPES in provisioning/lambda/updateSearchHandler.ts.
 * discussionPost is intentionally excluded: updates use Dynamo updatedAt for optimistic locking;
 * KB/search rows can carry stale or differently formatted timestamps and cause false concurrency errors. */
export const FULL_ITEM_FROM_SEARCH_MODELS = new Set([
    'product',
    'ecommerceCategory',
    'microcast',
]);
export const parseDuration = (duration) => {
    // console.log('parsing duration: ', duration);
    if (!duration) {
        return { hours: 0, minutes: 0, seconds: 0 };
    }
    const parts = duration.split(':');
    if (parts.length === 1) {
        return { hours: 0, minutes: parseInt(parts[0], 10), seconds: 0 };
    }
    if (parts.length === 3) {
        return {
            hours: parseInt(parts[0], 10),
            minutes: parseInt(parts[1], 10),
            seconds: parseInt(parts[2], 10),
        };
    }
    return { hours: parseInt(parts[0], 10), minutes: parseInt(parts[1], 10), seconds: 0 };
};
export const formatDuration = (duration) => {
    if (!duration) {
        return '';
    }
    const { hours, minutes, seconds } = parseDuration(duration);
    if (hours > 0) {
        return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
    }
    if (minutes > 0) {
        return `${minutes}m`;
    }
    if (seconds > 0) {
        return `${seconds}s`;
    }
    return '';
};
export const formatDate = (date) => {
    if (!date) {
        return '';
    }
    return new Date(date).toLocaleDateString('en-US');
};
/**
 * Takes a currency as either a float or a string and returns the value as a float.
 * It is forgiving as to the format of the argument.  In particular $ and , are removed
 * before converting string to float.
 */
export const parseCurrency = (amount) => {
    if (!amount) {
        return 0.0;
    }
    if (typeof amount === 'number') {
        return amount;
    }
    if (typeof amount === 'string') {
        return parseFloat(amount.replace(/,/g, '').replace('$', ''));
    }
    return parseFloat(amount);
};
/**
 * Formats a price for display.  It is forgiving on what the input is
 * to try and handle many cases such as whether its a string or a number,
 * and if it already has the dollar sign.
 *
 * @param amount
 * @returns
 */
export const formatCurrency = (amount) => {
    if (!amount) {
        return '';
    }
    // normalize by converting to a number
    const formattedAmount = parseCurrency(amount);
    return `$${formattedAmount.toFixed(2)}`;
};
// @Todo this is not the right way to do this
export const getCourseLevelLabel = (key) => {
    const field = jsonData.properties.course.properties.level;
    return field.enumLabels[field.enum.indexOf(key)];
};
const excludeField = (isPortalApi, propDef) => ((isPortalApi && (('portalApi' in propDef) && !propDef.portalApi)) || propDef.excludeField);
/** True when every value is nullish or a JSON leaf (no arrays / nested objects). Used to detect minimal reference blobs vs embedded snapshots (e.g. clientAIConfig with `key` + `wizards`). */
const plainObjectIsReferenceLeafOnly = (o) => Object.values(o).every((v) => {
    if (v === null || v === undefined)
        return true;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
        return true;
    if (v instanceof Date)
        return true;
    return false;
});
/** Store plain keys in config JSON instead of MST snapshots when a form leaf is an MST node (e.g. Asset picker). */
const normalizeFormLeafForPersistence = (value) => {
    if (value == null)
        return value;
    if (isStateTreeNode(value)) {
        try {
            const snap = getSnapshot(value);
            if (snap && typeof snap.key === 'string' && snap.key.length > 0) {
                return snap.key;
            }
            if (snap && typeof snap.id === 'string')
                return snap.id;
        }
        catch {
            // fall through
        }
    }
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const o = value;
        if (typeof o.key === 'string' &&
            o.key.length > 0 &&
            plainObjectIsReferenceLeafOnly(o)) {
            return o.key;
        }
    }
    return value;
};
/**
 * Apply RHF field paths (possibly dotted) to an MST entity. Deep paths into child models / frozen JSON
 * use snapshot merge + applySnapshot (lodash set on self does not work for clientAIConfig.* style paths).
 * Returns number of successful field updates.
 */
const applyFormFieldUpdatesToMst = (self, fields, newValues) => {
    let changeCount = 0;
    fields.forEach((key) => {
        if (!key)
            return;
        const pathRaw = get(newValues, key);
        const pathVal = normalizeFormLeafForPersistence(pathRaw);
        if (!key.includes('.')) {
            const oldVal = get(self, key);
            if (oldVal !== pathVal) {
                try {
                    set(self, key, pathVal);
                    changeCount += 1;
                }
                catch (e) {
                    console.error('Error on updateFields', { self, key, newValue: pathVal, e });
                }
            }
            return;
        }
        const dot = key.indexOf('.');
        const rootKey = key.slice(0, dot);
        const subPath = key.slice(dot + 1);
        const rootNode = self[rootKey];
        if (subPath && isStateTreeNode(rootNode)) {
            const snap = cloneDeep(getSnapshot(rootNode));
            const oldLeaf = get(snap, subPath);
            if (oldLeaf === pathVal)
                return;
            set(snap, subPath, pathVal);
            try {
                applySnapshot(rootNode, snap);
                changeCount += 1;
            }
            catch (e) {
                console.error('Error on updateFields', { self, key, newValue: pathVal, e });
            }
        }
        else {
            const oldVal = get(self, key);
            if (oldVal !== pathRaw) {
                try {
                    set(self, key, pathRaw);
                    changeCount += 1;
                }
                catch (e) {
                    console.error('Error on updateFields', { self, key, newValue: pathRaw, e });
                }
            }
        }
    });
    return changeCount;
};
/**
 *
 * @param fields JSON, it is a flat list of key value pairs
 */
export const updateFieldsGenerator = (self) => ((fields, newValues) => {
    applyFormFieldUpdatesToMst(self, fields, newValues);
    self.addChange();
});
export const splitKey = (reference) => {
    if (!reference) {
        return {
            id: null,
            organizationID: null,
        };
    }
    const parts = reference.split('~');
    if (parts.length === 1) {
        return {
            id: parts[0],
            organizationID: null,
        };
    }
    return {
        id: parts[1],
        organizationID: parts[0],
    };
};
export const resolveKey = (reference, organizationID, modelName) => {
    if (!reference) {
        return reference;
    }
    // @ts-expect-error reference is supposed to be a string...
    if (typeof reference !== 'string' && !(reference instanceof String)) {
        console.error('Resolve key expected string, got', reference);
        return reference;
    }
    if (!isOrganizationScoped(modelName)) {
        // console.log(`${modelName} not org scoped, returning: `, reference);
        return reference;
    }
    if (reference.indexOf('~') > 0) {
        return reference;
    }
    return `${organizationID}~${reference}`;
};
/**
 * Recursively adjusts the keys.  Note that sections is directly modified
 */
const resolveKeysOnSections = (sections, organizationID) => {
    sections?.forEach((section) => {
        if (section.item) {
            // Ensure section.item has valid key (MST identifier) - avoid undefined~undefined
            if (!section.item.key || String(section.item.key).includes('undefined')) {
                section.item.key = section.item.id;
            }
            section.item.asset = resolveKey(section.item.asset, organizationID, 'asset');
            section.item.certification
                = resolveKey(section.item.certification, organizationID, 'certification');
            section.item.course = resolveKey(section.item.course, organizationID, 'course');
            section.item.microcast =
                resolveKey(section.item.microcast, organizationID, 'microcast');
            section.item.posterImage =
                resolveKey(section.item.posterImage, organizationID, 'asset');
            section.item.microcastImage =
                resolveKey(section.item.microcastImage, organizationID, 'asset');
            section.item.reference =
                resolveKey(section.item.reference, organizationID, 'courseSection');
        }
        // do its children
        resolveKeysOnSections(section.sections, organizationID);
    });
};
const schemaDef = (json) => {
    const models = json.properties;
    const getModel = (name) => {
        const model = models[name];
        if (model) {
            return model;
        }
        throw Error(`No model of type ${name}`);
    };
    /**
     * Returns an array of property groups.
     * Each property group contains the list of properties that belongs to it.
     *
     */
    const getPropertyGroups = (modelName) => {
        const model = getModel(modelName);
        if (model.compiledPropertyGroups) {
            return model.compiledPropertyGroups;
        }
        const propertiesByGroup = Object.keys(model.properties).reduce((map, propName) => {
            const propDef = model.properties[propName];
            if (propDef.group) {
                map[propDef.group] = map[propDef.group] || [];
                map[propDef.group].push(propName);
            }
            return map;
        }, {});
        model.compiledPropertyGroups = propertiesByGroup;
        return propertiesByGroup;
    };
    const getGqlPropertyList = (modelName, isPortalApi = false, includeReadOnly = true, includeJoins = true, itemLoad = false) => {
        // console.log('props', models[modelName].properties);
        const model = getModel(modelName);
        const keys = Object.keys(model.properties);
        const propertyList = keys.reduce((list, key) => {
            const propDef = model.properties[key];
            // its the portal api and either portalApi is present and false, or its org id
            // or its marked with 'excludeField'
            if (excludeField(isPortalApi, propDef) || (itemLoad && propDef.itemLoad === false)) {
                // console.log('Skipping prop', propDef);
                return list;
            }
            if (propDef.type === 'chimeMeeting') {
                list.push('chimeMeeting { ExternalMeetingId, MediaRegion, MediaPlacement { AudioHostUrl, AudioFallbackUrl, ScreenDataUrl, ScreenSharingUrl, ScreenViewingUrl, SignalingUrl, TurnControlUrl } }');
                return list;
            }
            // if (propDef.type === 'join') {
            // console.log('Skipping join', propDef);
            // return list;
            // }
            if (propDef.readOnly && !propDef.primaryKey && !includeReadOnly) {
                // console.log('Skipping non-pk readonly', propDef);
                return list;
            }
            if (propDef.type === 'join') {
                if (includeJoins) {
                    list.push(`${propDef.fieldName} { items { organizationID, id } }`);
                }
            }
            else if (propDef.fieldName) {
                list.push(propDef.fieldName);
            }
            else {
                list.push(key);
            }
            return list;
        }, []);
        return propertyList;
    };
    const getPropertyList = (modelName, editMode) => {
        // console.log('props', models[modelName].properties);
        const model = getModel(modelName);
        let { properties } = model;
        if (editMode) {
            properties = model.editModalProperties;
        }
        const keys = Object.keys(properties);
        const propertyList = keys.reduce((prevValue, key) => {
            if (properties[key].type !== 'referenceXX') {
                prevValue.push(key);
            }
            return prevValue;
        }, []);
        return propertyList;
    };
    const getSearchablePropertyList = (modelName) => {
        const model = getModel(modelName);
        const { properties } = model;
        const keys = Object.keys(properties);
        const propertyList = keys.reduce((prevValue, key) => {
            if (properties[key].searchable) {
                prevValue.push(key);
            }
            return prevValue;
        }, []);
        return propertyList.length ? propertyList : ['name'];
    };
    const getSortValues = (modelName) => {
        const model = getModel(modelName);
        const { sortBy } = model;
        return sortBy || { field: 'name', direction: 'asc' };
    };
    /**
     * Returns an instance of the model with fields set to their default values.
     */
    const getInstance = (modelName) => {
        if (modelName in defaults) {
            return cloneDeep(defaults[modelName]);
        }
        const model = getModel(modelName);
        const instance = {};
        Object.keys(model.properties).forEach((propName) => {
            const propDef = model.properties[propName];
            let defaultValue;
            if ('default' in propDef) {
                defaultValue = propDef.default;
            }
            else if (propDef.type === 'string') {
                // no default - for now set numbers to 0 and strings to ''
                defaultValue = '';
            }
            else if (propDef.type === 'number' || propDef.type === 'currency') {
                defaultValue = 0;
            }
            // rest will leave as null
            instance[propName] = defaultValue;
        });
        defaults[modelName] = instance;
        return instance;
    };
    /**
     * Returns the definition for the (possible nested) property name.
     * Indexed props not currently supported.
     */
    const getProperty = (modelName, propPath) => {
        const model = getModel(modelName);
        const propName = propPath.split('.');
        const prop = model.properties[propName[0] === 'siteGlobalColor' ? 'global' : propName[0]];
        if (!prop)
            throw Error(`Can't find property ${modelName} . ${propPath}`);
        if (prop.type === 'object' || prop.type === 'entity' || prop.type === 'dependant') {
            return getProperty(prop.references, propPath.substr(propPath.indexOf('.') + 1));
        }
        return prop;
    };
    const getPluralName = (modelName) => {
        const model = getModel(modelName);
        return (model.pluralName ? model.pluralName : pluralize(modelName) /* modelName + 's' */);
    };
    const modelNameCapped = (modelName) => modelName[0].toUpperCase() + modelName.substring(1);
    const getTypeName = (modelName) => {
        const model = getModel(modelName);
        return model.typeName || modelNameCapped(modelName);
    };
    const getPluralTypeName = (modelName) => {
        const model = getModel(modelName);
        const typeName = model.typeName || modelNameCapped(modelName);
        return model.pluralTypeName || `${typeName}s`;
    };
    const getLabel = (modelName) => (getModel(modelName).label || modelNameCapped(modelName));
    const getPluralLabel = (modelName) => {
        const model = getModel(modelName);
        return (model.pluralLabel ? model.pluralLabel : pluralize(getLabel(modelName)));
    };
    const listCourseProgressGQL = `
query ListCourseProgresses(
  $courseID: ID
  $id: ID
  $limit: Int
  $nextToken: String
) {
  listCourseProgresses(courseID: $courseID, id: $id, limit: $limit, nextToken: $nextToken) {
    items {
      ${getGqlPropertyList('courseProgress', true).join(' ')}
    }
    nextToken
  }
}
`;
    /** Canonical BatchGetResult field name (matches GraphQL and Lambda BATCH_GET_TABLE_ENV). */
    const getBatchGetFieldName = (modelName) => (modelName || '').trim().charAt(0).toLowerCase() + (modelName || '').trim().slice(1);
    const getBatchLoadGQL = (modelName, isPortalApi = false) => {
        const fieldName = getBatchGetFieldName(modelName);
        const gql = `
  query BatchGetModel(
    $organizationID: ID!
    $modelName: String!
    $ids: [ID]!
  ) {
    batchGetModel(
      organizationID: $organizationID
      modelName: $modelName,
      ids: $ids
    ) {
      ${fieldName} {
        ${getGqlPropertyList(modelName, isPortalApi).join(' ')}  
      }
    }
  }
  `;
        return gql;
    };
    return {
        getPortalAIConfigSource: (portal) => portalAIConfigSources.get(portal),
        getPortalAIConfigEdits,
        acceptPortalAIConfigSave: (portal, config, snapshot, submitted) => {
            portalAIConfigSources.set(portal, { config: cloneDeep(config), snapshot: cloneDeep(snapshot) });
            const edits = getPortalAIConfigEdits(portal);
            submitted?.forEach((value, field) => {
                if (edits.has(field) && isEqual(edits.get(field), value))
                    edits.delete(field);
            });
        },
        normalizeFormValue: normalizeFormLeafForPersistence,
        getBatchGetFieldName,
        getCapped: modelNameCapped,
        getModels: () => (models),
        getModel,
        getPluralName,
        getLabel,
        getPluralLabel,
        getPluralTypeName,
        getProperty,
        getPropertyList,
        getSearchablePropertyList,
        getTypeName,
        getBatchLoadGQL,
        getInstance,
        getPropertyGroups,
        getGqlPropertyList,
        getSortValues,
        getEmptyInstance: (modelName, organization) => {
            const model = getModel(modelName);
            const instance = {};
            Object.keys(model.properties).forEach((propName) => {
                // console.log(`prop name: ${prop}`)
                const propDef = model.properties[propName];
                if (propDef && propDef.default) {
                    instance[propName] = propDef.default;
                }
                else if (propDef && propDef.default === '') {
                    // this needs to be a seperate case because empty string defaults to false
                    instance[propName] = propDef.default;
                }
                else {
                    instance[propName] = null;
                }
            });
            if (isOrganizationScoped(modelName)) {
                instance.organization = organization;
            }
            return instance;
        },
        /**
         * Returns true if the model has properties organized into property groups.
         * @param modelName
         * @returns
         */
        hasPropertyGroups: (modelName) => {
            const model = getModel(modelName);
            return model.hasOwnProperty('propertyGroups');
        },
        getFormConfig: (modelName) => {
            const model = getModel(modelName);
            const { formConfig } = model;
            if (formConfig) {
                return formConfig;
            }
            // flat list of fields
            return Object.keys(model.properties);
        },
        /**
         * TODO: Apply some functional programming magic here
         * @param modelName
         * @param newValues
         * @param instance
         * @returns
         */
        mapValuesFromGet: (modelName, newValues, instance) => {
            const model = getModel(modelName);
            const capturePortalConfig = modelName === 'portal' && instance && instance.changeCount === 0
                && Object.prototype.hasOwnProperty.call(newValues, 'clientAIConfig');
            // get the processed values
            let key = isOrganizationScoped(modelName) ? `${newValues.organizationID}~${newValues.id}` : newValues.id;
            // MST forbids changing identifiers - if key would be invalid, preserve instance's key
            if (instance && (!key || String(key).includes('undefined'))) {
                key = instance.key;
            }
            const processedValues = { key };
            // console.log(`Model ${modelName}, newValues:`, newValues);
            Object.keys(model.properties).forEach((propName) => {
                const propDef = model.properties[propName];
                const fieldName = propDef.fieldName || propName;
                const fieldValue = newValues[fieldName];
                // console.log('  prop: ', { fieldName, fieldValue, default: propDef.default });
                let processedValue = fieldValue; // this will be the default
                if (fieldValue !== undefined && fieldValue !== null) {
                    // have the value use it as a literal or map it if needed
                    if (propDef.type === 'reference') {
                        // fieldValue could also be the object directly.
                        // console.log('  prop: ', { fieldName, fieldValue, default: propDef.default });
                        if (isString(fieldValue) && isOrganizationScoped(propDef.references) && fieldValue?.indexOf('~') === -1) {
                            if (propDef.shortKey) {
                                processedValue = fieldValue;
                            }
                            else {
                                // make it relative to the same org as this item
                                processedValue = `${newValues.organizationID}~${fieldValue}`;
                            }
                        }
                        else {
                            processedValue = fieldValue || undefined;
                        }
                        // console.log(`Reference ${propDef.references} is`, processedValue);
                    }
                    else if (propDef.type === 'referenceArray') {
                        if (isOrganizationScoped(propDef.references)) {
                            processedValue = processedValue?.map((value) => {
                                if (isString(value) && isOrganizationScoped(propDef.references) && value?.indexOf('~') === -1) {
                                    if (propDef.shortKey) {
                                        return value;
                                    }
                                    // make it relative to the same org as this item
                                    return `${newValues.organizationID}~${value}`;
                                }
                                return value;
                            });
                        }
                        else {
                            processedValue = fieldValue || undefined;
                        }
                        // console.log(`Reference ${propDef.references} is`, processedValue);
                    }
                    else if (propDef.type === 'date' || propDef.type === 'timestamp') {
                        if (!fieldValue) {
                            processedValue = null;
                        }
                        else if (fieldValue instanceof Date) {
                            processedValue = fieldValue;
                        }
                        else if (typeof fieldValue === 'number') {
                            processedValue = new Date(fieldValue);
                        }
                        else {
                            const parsed = new Date(fieldValue);
                            processedValue = Number.isNaN(parsed.getTime()) ? null : parsed;
                        }
                    }
                    else if (propDef.type === 'richtext') {
                        // if its an array but the data type is string try to convert
                        if (typeof fieldValue === 'string') {
                            processedValue = JSON.parse(fieldValue);
                        }
                        if (fieldValue instanceof String) {
                            processedValue = JSON.parse(fieldValue.toString());
                        }
                        // do it again if needed
                        if (typeof processedValue === 'string') {
                            processedValue = JSON.parse(processedValue);
                        }
                        if (processedValue instanceof String) {
                            processedValue = JSON.parse(processedValue.toString());
                        }
                    }
                    else if (propDef.type === 'formattedText') {
                        processedValue = typeof fieldValue === 'string' ? fieldValue : `${fieldValue}`;
                    }
                    else if (propDef.type === 'json' || propDef.type === 'dependant') {
                        // if its an array but the data type is string try to convert
                        if (modelName === 'itemProgress' && propName === 'tracking') {
                            // AppSync can wrap tracking in a second JSON string. Consumers
                            // of itemProgress expect the decoded object, including Excel.
                            processedValue = parseAwsJsonObject(fieldValue instanceof String ? fieldValue.toString() : fieldValue);
                        }
                        else if (typeof fieldValue === 'string') {
                            processedValue = JSON.parse(fieldValue);
                        }
                        else if (fieldValue instanceof String) {
                            processedValue = JSON.parse(fieldValue.toString());
                        }
                        else {
                            processedValue = fieldValue;
                        }
                        // } else if (propDef.type === 'options') {
                        //   // if its an array but the data type is string try to convert
                        //   if (typeof fieldValue === 'string') {
                        //     processedValue = JSON.parse(fieldValue);
                        //   } else if (fieldValue instanceof String) {
                        //     processedValue = JSON.parse(fieldValue.toString());
                        //   } else {
                        //     processedValue = fieldValue;
                        //   }
                    }
                    else if (propDef.type === 'currency') {
                        processedValue = fieldValue;
                    }
                    else if (propDef.type === 'accessGroups') {
                        processedValue = fieldValue;
                    }
                    else if (propDef.type === 'array') {
                        // if its an array but the data type is string try to convert
                        if (propDef.references === 'string') {
                            processedValue = fieldValue;
                        }
                        else {
                            if (typeof fieldValue === 'string') {
                                processedValue = JSON.parse(fieldValue);
                            }
                            if (fieldValue instanceof String) {
                                processedValue = JSON.parse(fieldValue.toString());
                            }
                            // set 'key' on each element in the array
                            processedValue = processedValue?.map((item) => {
                                if (item.id) {
                                    item.key = item.id;
                                }
                                return item;
                            });
                            // hack for now on sections to fix keys
                            if (['courseSection', 'programSection'].includes(propDef.references)) {
                                resolveKeysOnSections(processedValue, newValues.organizationID);
                                // console.log('SECTIONS IS NOW', processedValue);
                            }
                        }
                        // console.log('Processed value for array', processedValue);
                    }
                    else if (propDef.type === 'join') {
                        // console.log(`Load join for ${propName}`, fieldValue);
                        processedValue = (fieldValue && fieldValue.items)
                            ? fieldValue.items.map((item) => (item.id))
                            : cast([]);
                        // instance[fieldName] = JSON.stringify(fieldValue);
                    }
                    else if (propDef.type === 'string' && Array.isArray(fieldValue)) {
                        // E-commerce APIs (e.g. Shopify) return tags as array; schema expects string
                        processedValue = fieldValue.map(String).filter(Boolean).join(', ');
                    }
                    else {
                        processedValue = fieldValue;
                    }
                }
                else if (propDef && propDef.default) {
                    // console.log(`${propName} getting set to `, propDef.default);
                    processedValue = propDef.default;
                }
                else {
                    processedValue = undefined;
                }
                processedValues[propName] = processedValue;
            });
            // todo - revisit this logic
            if (modelName === 'itemProgress') {
                // tweak the id
                processedValues.id = `itemProgress-${processedValues.id}`;
            }
            else if (modelName === 'item') {
                if (processedValues.type === 'course') {
                    processedValues.program = null;
                }
                else if (instance.type === 'program') {
                    processedValues.course = null;
                }
            }
            // console.log(`Model ${modelName}, processedValues:`, processedValues);
            if (instance) {
                // assume the instance is one of our models
                instance.updateValues(processedValues);
                instance.resetChange();
                if (capturePortalConfig) {
                    getPortalAIConfigEdits(instance).clear();
                    const raw = newValues.clientAIConfig;
                    portalAIConfigSources.set(instance, {
                        config: cloneDeep(typeof raw === 'string' ? JSON.parse(raw) : raw),
                        snapshot: cloneDeep(getSnapshot(instance)),
                    });
                }
                // console.log(`Instance ${modelName}, instance:`, instance);
                return instance;
            }
            return processedValues;
        },
        getValuesForUpdate: (modelName, newValues, isPortalApi = false, 
        // readOnly can be set on create, but not update
        includeReadOnly = true) => {
            const model = getModel(modelName);
            const instance = {};
            Object.keys(model.properties).forEach((propName) => {
                const propDef = model.properties[propName];
                // console.log(`prop name: ${propName}`, propDef);
                // skip some properties in portalApi
                // portalApi in schema should default to true, so to check first
                // check it exists, and then if it does exist check its value)
                if (excludeField(isPortalApi, propDef)) {
                    // console.log('Skipping for portalApi', propDef);
                    return;
                }
                if (propDef.genericUpdate === false) {
                    return;
                }
                if (propDef.readOnly && !propDef.primaryKey && !includeReadOnly) {
                    // console.log('Skipping non-pk readonly', propDef);
                    return;
                }
                // allow field name to override name in graphql
                const fieldName = propDef.fieldName || propName;
                // if its not defined, use the default value
                const fieldValue = typeof newValues[propName] === 'undefined' ? propDef.default : newValues[propName];
                // have the value use it as a literal or map it if needed
                if (propDef.type === 'reference') {
                    // it can either be the object itself or the key
                    // note if the 'undefined' is passed the field is not updated,
                    // if null is passed the field value is set to null
                    const reference = (typeof fieldValue === 'string')
                        ? fieldValue
                        : (fieldValue && fieldValue.key) || null;
                    if (propDef.shortKey) {
                        const { id } = splitKey(reference);
                        instance[fieldName] = id;
                    }
                    else {
                        instance[fieldName] = reference;
                    }
                }
                else if (propDef.type === 'referenceArray') {
                    // get the items and convert to ids
                    // it can either be the object itself or the key
                    if (Array.isArray(fieldValue)) {
                        instance[fieldName] = fieldValue.map((item) => {
                            const reference = (typeof item === 'string')
                                ? item
                                : (item && item.key) || null;
                            if (propDef.shortKey) {
                                const { id } = splitKey(reference);
                                return id;
                            }
                            return reference;
                        });
                    }
                }
                else if (propDef.type === 'richtext') {
                    instance[fieldName] = JSON.stringify(fieldValue);
                }
                else if (propDef.type === 'formattedText') {
                    instance[fieldName] = typeof fieldValue === 'string'
                        ? fieldValue
                        : (fieldValue?.html || '');
                }
                else if (propDef.type === 'options') {
                    instance[fieldName] = fieldValue;
                }
                else if (propDef.type === 'json') {
                    instance[fieldName] = JSON.stringify(fieldValue);
                }
                else if (propDef.type === 'dependant') {
                    instance[fieldName] = JSON.stringify(fieldValue);
                }
                else if (propDef.type === 'array') {
                    if (propDef.references === 'string') {
                        instance[fieldName] = fieldValue;
                    }
                    else {
                        const value = isStateTreeNode(fieldValue) ? getSnapshot(fieldValue) : fieldValue;
                        instance[fieldName] = JSON.stringify(value);
                    }
                }
                else if (propDef.type === 'date') {
                    const dateValue = fieldValue;
                    if (!fieldValue) {
                        instance[fieldName] = null;
                    }
                    else if (dayjs.isDayjs(dateValue)) {
                        instance[fieldName] = dateValue.format('YYYY-MM-DD');
                    }
                    else {
                        instance[fieldName] = fieldValue ? fieldValue.toISOString().split('T')[0] : null;
                    }
                }
                else if (propDef.type === 'timestamp') {
                    // const timestampValue = newValues[propName];
                    instance[fieldName] = fieldValue ? fieldValue.toISOString() : null;
                }
                else if (propDef.type === 'join') {
                    // ignore joins
                }
                else {
                    instance[fieldName] = fieldValue;
                }
            });
            return instance;
        },
        getPrimaryKeysForUpdate: (modelName, item) => {
            const model = getModel(modelName);
            const instance = {};
            Object.keys(model.properties).forEach((propName) => {
                const propDef = model.properties[propName];
                // console.log(`prop name: ${propName}`, propDef);
                if (!propDef.primaryKey) {
                    // console.log('Skipping non-pk readonly', propDef);
                    return;
                }
                // allow field name to override name in graphql
                const fieldName = propDef.fieldName || propName;
                // if its not defined, use the default value
                const fieldValue = typeof item[propName] === 'undefined' ? propDef.default : item[propName];
                // have the value use it as a literal or map it if needed
                if (propDef.type === 'reference') {
                    // it can either be the object itself or the key
                    // note if the 'undefined' is passed the field is not updated,
                    // if null is passed the field value is set to null
                    const reference = (typeof fieldValue === 'string')
                        ? fieldValue
                        : (fieldValue && fieldValue.key) || null;
                    if (propDef.shortKey) {
                        const { id } = splitKey(reference);
                        instance[fieldName] = id;
                    }
                    else {
                        instance[fieldName] = reference;
                    }
                }
                else if (propDef.type === 'date') {
                    const dateValue = fieldValue;
                    if (!fieldValue) {
                        instance[fieldName] = null;
                    }
                    else if (dayjs.isDayjs(dateValue)) {
                        instance[fieldName] = dateValue.format('YYYY-MM-DD');
                    }
                    else {
                        instance[fieldName] = fieldValue ? fieldValue.toISOString().split('T')[0] : null;
                    }
                }
                else if (propDef.type === 'timestamp') {
                    // const timestampValue = newValues[propName];
                    instance[fieldName] = fieldValue ? fieldValue.toISOString() : null;
                }
                else {
                    instance[fieldName] = fieldValue;
                }
            });
            return instance;
        },
        getPrimaryKeysForDelete: (modelName, item) => {
            const model = getModel(modelName);
            const instance = {};
            ['id', 'organizationID'].forEach((key) => {
                const propDef = model.properties[key];
                if (propDef && (propDef.type === 'string' || propDef.type === 'reference')) {
                    const fieldName = propDef.fieldName || key;
                    const fieldValue = typeof item[key] === 'undefined' ? propDef.default : item[key];
                    if (propDef.type === 'reference') {
                        const reference = (typeof fieldValue === 'string') ? fieldValue : (fieldValue && fieldValue.key) || null;
                        if (propDef.shortKey) {
                            const { id } = splitKey(reference);
                            instance[fieldName] = id;
                        }
                        else {
                            instance[fieldName] = reference;
                        }
                    }
                    else {
                        instance[fieldName] = fieldValue || null;
                    }
                }
            });
            return instance;
        },
        getItemGQL: (modelName, isPortalApi = false) => {
            const model = getModel(modelName);
            const typeName = model.typeName || modelNameCapped(modelName);
            if (isOrganizationScoped(modelName)) {
                const gql = `
  query Get${typeName}($id: ID!, $organizationID: ID!) {
    get${typeName}(id: $id, organizationID: $organizationID) {
      ${getGqlPropertyList(modelName, isPortalApi, true, true, true).join(' ')}
    }
  }
`;
                // console.log(gql);
                return gql;
            }
            // special case for organization in portal api - dont want the id
            if (isPortalApi && modelName === 'organization') {
                const gql = `
  query Get${typeName} {
    get${typeName} {
      ${getGqlPropertyList(modelName, isPortalApi, true, true, true).join(' ')}
    }
  }
`;
                // console.log(gql);
                return gql;
            }
            const gql = `
  query Get${typeName}($id: ID!) {
    get${typeName}(id: $id) {
      ${getGqlPropertyList(modelName, isPortalApi, true, true, true).join(' ')}
    }
  }
`;
            return gql;
        },
        getSearchGQL: (_modelName, _isPortalApi = false) => `
query Search($input: SearchInput!) {
  search(input: $input) {
    items
    total
    nextToken
  }
}
`,
        getFindBySlugGQL: (modelName) => {
            const modelNameNorm = modelName === 'class' ? 'course' : modelName;
            const typeName = getModel(modelNameNorm).typeName || modelNameCapped(modelNameNorm);
            return `
query FindBySlug($organizationID: ID!, $objectType: ObjectType!, $slug: String!, $libraryIds: [String!]) {
  findBySlug(organizationID: $organizationID, objectType: $objectType, slug: $slug, libraryIds: $libraryIds)
}
`;
        },
        /**
         * Admin item progress (Dynamo via AdminSearch Lambda).
         */
        getItemProgressSearchGQL: (modelName, isPortalApi = false) => {
            if (modelName !== 'itemProgress') {
                throw new Error('getItemProgressSearchGQL: only model itemProgress is supported');
            }
            return `
query ItemProgressSearch($input: ItemProgressSearchInput!) {
  itemProgressSearch(input: $input) {
    items {
      ${getGqlPropertyList(modelName, isPortalApi).join(' ')}
    }
    nextToken
    total
    aggregates {
      totalRows
      completedCount
      inProgressCount
      notStartedCount
      averageProgress
      certifiedCount
    }
  }
}
`;
        },
        getListGQL: (modelName, isPortalApi = false) => {
            if (modelName === 'courseProgress' && isPortalApi) {
                // console.log('Returning courseProgress GQL');
                return listCourseProgressGQL;
            }
            const model = getModel(modelName);
            const typeName = model.typeName || modelNameCapped(modelName);
            const pluralTypeName = model.pluralTypeName || `${typeName}s`;
            const filterName = `${isPortalApi ? '' : 'Model'}${typeName}FilterInput`;
            const gql = `
        query List${pluralTypeName}(
          $filter: ${filterName}
          $limit: Int
          $nextToken: String
        ) {
          list${pluralTypeName}(filter: $filter, limit: $limit, nextToken: $nextToken) {
            items {
              ${getGqlPropertyList(modelName, isPortalApi).join(' ')}
            }
            nextToken
          }
        }
      `;
            return gql;
        },
        getCreateGQL: (modelName, isPortalApi) => {
            const model = getModel(modelName);
            const typeName = model.typeName || modelNameCapped(modelName);
            const gql = `
mutation Create${typeName}(
  $input: Create${typeName}Input!
  $condition: ${isPortalApi ? '' : 'Model'}${typeName}ConditionInput
) {
  create${typeName}(input: $input, condition: $condition) {
    ${getGqlPropertyList(modelName, isPortalApi, true, false).join(' ')}
  }
}
`;
            // console.log('Create GQL', gql);
            return gql;
        },
        getUpdateGQL: (modelName, isPortalApi) => {
            const model = getModel(modelName);
            const typeName = model.typeName || modelNameCapped(modelName);
            const gql = `
mutation Update${typeName}(
  $input: Update${typeName}Input!
  $condition: ${isPortalApi ? '' : 'Model'}${typeName}ConditionInput
) {
  update${typeName}(input: $input, condition: $condition) {
    ${getGqlPropertyList(modelName, isPortalApi, false, false).join(' ')}
  }
}
`;
            return gql;
        },
        getDeleteGQL: (modelName, isPortalApi) => {
            const model = getModel(modelName);
            const typeName = model.typeName || modelNameCapped(modelName);
            const gql = `
mutation Delete${typeName}(
  $input: Delete${typeName}Input!
  $condition: ${isPortalApi ? '' : 'Model'}${typeName}ConditionInput
) {
  delete${typeName}(input: $input, condition: $condition) {
    id
    ${isOrganizationScoped(modelName) ? 'organizationID' : ''} 
  }
}
`;
            return gql;
        },
    };
};
export default schemaDef(jsonData);
//# sourceMappingURL=Schema.js.map